import React, { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Table, Button, Tag, Typography, Breadcrumb, Row, Col, Statistic, Empty } from 'antd'
import { ArrowLeftOutlined, CalendarOutlined, UserOutlined, PhoneOutlined, EnvironmentOutlined } from '@ant-design/icons'
import { leadStore } from '@/pages/Lead/leadStore'

const { Title, Text } = Typography

const DCRDetail: React.FC = () => {
  const { id } = useParams() // id is expected to be `${date}_${employee}`
  const navigate = useNavigate()

  const [dateStr, ...empParts] = (id || '').split('_')
  const employee = empParts.join('_')

  // Derive the details directly from leadStore
  const details = useMemo(() => {
    const leads = leadStore.getLeads()
    const interactions: any[] = []
    let totalCalls = 0
    let totalVisits = 0
    let totalLeadsGenerated = 0

    leads.forEach(lead => {
      // 1. Check generated leads
      const createDate = new Date(lead.createdAt).toISOString().split('T')[0]
      if (createDate === dateStr && lead.assignedTo === employee) {
        totalLeadsGenerated++
      }

      // 2. Check interactions (calls, visits)
      if (lead.discussionHistory) {
        lead.discussionHistory.forEach(disc => {
          if ((disc.type === 'call' || disc.type === 'visit') && disc.createdBy === employee) {
            const discDate = new Date(disc.createdAt).toISOString().split('T')[0]
            if (discDate === dateStr) {
              interactions.push({
                key: disc.id,
                time: new Date(disc.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                type: disc.type,
                leadName: lead.companyName || lead.customerName || lead.id,
                leadId: lead.id,
                content: disc.content || (disc.type === 'call' ? 'Called customer' : 'Visited customer'),
              })
              if (disc.type === 'call') totalCalls++
              if (disc.type === 'visit') totalVisits++
            }
          }
        })
      }
    })

    return { interactions, totalCalls, totalVisits, totalLeadsGenerated }
  }, [dateStr, employee])

  const columns = [
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      width: 100,
      render: (text: string) => <Text type="secondary">{text}</Text>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => (
        <Tag color={type === 'call' ? 'blue' : 'green'} icon={type === 'call' ? <PhoneOutlined /> : <EnvironmentOutlined />}>
          {type === 'call' ? 'Call' : 'Visit'}
        </Tag>
      )
    },
    {
      title: 'Lead / Customer',
      dataIndex: 'leadName',
      key: 'leadName',
      width: 250,
      render: (text: string, record: any) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/leads/${record.leadId}`)}>
          {text}
        </Button>
      )
    },
    {
      title: 'Remarks / Notes',
      dataIndex: 'content',
      key: 'content',
    }
  ]

  if (!dateStr || !employee) {
    return <Empty description="Invalid DCR ID" />
  }

  return (
    <div style={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item><a onClick={() => navigate('/dcr')}>Daily Call Reports</a></Breadcrumb.Item>
        <Breadcrumb.Item>Report Details</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
          <div>
            <Title level={3} style={{ margin: 0, color: '#001A4D' }}>DCR Overview</Title>
            <Text type="secondary">Detailed breakdown of interactions</Text>
          </div>
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <Statistic title="Date" value={dateStr} prefix={<CalendarOutlined />} valueStyle={{ fontSize: 18 }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <Statistic title="Employee" value={employee} prefix={<UserOutlined />} valueStyle={{ fontSize: 18 }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Calls</Text>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#0099CC' }}>{details.totalCalls}</div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Visits</Text>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#001A4D' }}>{details.totalVisits}</div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>New Leads</Text>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#D97706' }}>{details.totalLeadsGenerated}</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card title="Interactions Breakdown" bordered={false} style={{ borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <Table
          dataSource={details.interactions}
          columns={columns}
          pagination={false}
          locale={{ emptyText: 'No calls or visits recorded for this day.' }}
        />
      </Card>
    </div>
  )
}

export default DCRDetail
